import api from './api';
import imageStorage from './imageStorage';

class CloudSyncService {
  // Upload single image to cloud (for premium users)
  async uploadToCloud(receiptId) {
    const localImage = await imageStorage.getImage(receiptId);
    if (!localImage) {
      throw new Error('Image not found locally');
    }

    const response = await api.post('/cloud/upload', {
      receipt_id: receiptId,
      image_data: localImage.imageData
    });

    if (response.data.success) {
      await imageStorage.markAsSynced(receiptId);
    }

    return response.data;
  }

  // Sync all unsynced images to cloud
  async syncAllToCloud(onProgress) {
    const unsyncedImages = await imageStorage.getUnsyncedImages();
    const total = unsyncedImages.length;
    let completed = 0;
    let failed = 0;

    for (const image of unsyncedImages) {
      try {
        await this.uploadToCloud(image.receiptId);
        completed++;
      } catch (error) {
        console.error(`Failed to sync ${image.receiptId}:`, error);
        failed++;
      }
      
      if (onProgress) {
        onProgress({ completed, failed, total });
      }
    }

    return { completed, failed, total };
  }

  // Batch sync via backend (more efficient for many images)
  async batchSyncToCloud() {
    try {
      const response = await api.post('/cloud/sync-all');
      return response.data;
    } catch (error) {
      // Check if it's an SSL/connection error (preview environment limitation)
      if (error.message?.includes('SSL') || error.response?.status === 500) {
        return {
          success: false,
          synced: 0,
          failed: 0,
          error: 'Cloud sync unavailable in preview. Will work in deployed version.',
          isPreviewLimitation: true
        };
      }
      console.error('Batch sync failed:', error);
      throw error;
    }
  }

  // Download image from cloud to local
  async downloadFromCloud(receiptId) {
    const response = await api.get(`/cloud/download/${receiptId}`);
    
    if (response.data.image_data) {
      await imageStorage.saveImage(receiptId, response.data.image_data, {
        synced: true,
        syncedAt: new Date().toISOString()
      });
    }

    return response.data;
  }

  // Get presigned URL for direct cloud access
  async getPresignedUrl(receiptId) {
    try {
      const response = await api.get(`/cloud/presigned-url/${receiptId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get presigned URL:', error);
      return null;
    }
  }

  // Get cloud storage status
  async getCloudStatus() {
    try {
      const response = await api.get('/cloud/status');
      return response.data;
    } catch (error) {
      return { enabled: false, provider: 'none', storage_used_mb: 0, images_count: 0 };
    }
  }

  // Check if user has cloud storage enabled
  async isCloudEnabled() {
    const status = await this.getCloudStatus();
    return status.enabled;
  }

  // Get local storage stats
  async getLocalStats() {
    return await imageStorage.getStorageStats();
  }

  // Combined stats (local + cloud)
  async getCombinedStats() {
    const [localStats, cloudStatus] = await Promise.all([
      this.getLocalStats(),
      this.getCloudStatus()
    ]);

    return {
      local: localStats,
      cloud: cloudStatus,
      totalImages: localStats.totalImages,
      syncedToCloud: localStats.syncedCount,
      pendingSync: localStats.unsyncedCount
    };
  }
}

const cloudSync = new CloudSyncService();
export default cloudSync;
